import { parseCsv, toCsv } from "@/utils/csv"
import type {
  CopyFieldMode,
  Round,
  SignatureFieldMode,
  Variation,
} from "@/types/entities/spamTestSettings"

const COPY_FIELDS = ["subject", "greeting", "body", "signoff", "unsub"] as const
const SIGNATURE_FIELDS = ["title", "company", "website", "phoneNumber"] as const

const COPY_MODES: CopyFieldMode[] = ["original", "prompt", "blank"]
const SIGNATURE_MODES: SignatureFieldMode[] = ["original", "blank"]

export const PROMPTS_CSV_HEADERS: string[] = [
  "round",
  "variationName",
  ...COPY_FIELDS.flatMap((field) => [`${field}_mode`, `${field}_prompt`]),
  ...SIGNATURE_FIELDS.map((field) => `${field}_mode`),
]

const copyMode = (value: string): CopyFieldMode =>
  COPY_MODES.includes(value as CopyFieldMode)
    ? (value as CopyFieldMode)
    : "original"

const signatureMode = (value: string): SignatureFieldMode =>
  SIGNATURE_MODES.includes(value as SignatureFieldMode)
    ? (value as SignatureFieldMode)
    : "original"

export const roundsToCsv = (rounds: Round[]): string => {
  const rows: string[][] = [PROMPTS_CSV_HEADERS]

  rounds.forEach((round) => {
    round.variations.forEach((variation, variationIndex) => {
      const fallbackName = `R${round.roundNumber}V${variationIndex + 1}`
      const row: string[] = [
        String(round.roundNumber),
        variation.variationName?.trim() || fallbackName,
      ]
      COPY_FIELDS.forEach((field) => {
        const config = variation.fields[field]
        row.push(config.mode)
        row.push(config.mode === "prompt" ? config.prompt ?? "" : "")
      })
      SIGNATURE_FIELDS.forEach((field) => {
        row.push(variation.fields[field].mode)
      })
      rows.push(row)
    })
  })

  return toCsv(rows)
}

export type ParsedPromptsCsv = {
  rounds: Round[]
  error?: string
}

export const csvToRounds = (text: string): ParsedPromptsCsv => {
  const matrix = parseCsv(text).filter((row) =>
    row.some((cell) => cell.trim() !== ""),
  )

  if (!matrix.length) {
    return { rounds: [], error: "CSV is empty." }
  }

  const header = matrix[0].map((cell) => cell.trim())
  const indexOf = (name: string) => header.indexOf(name)

  const roundIdx = indexOf("round")
  const nameIdx = indexOf("variationName")
  if (roundIdx === -1 || nameIdx === -1) {
    return {
      rounds: [],
      error: "CSV must include round and variationName columns.",
    }
  }

  const roundsByNumber = new Map<number, Round>()

  for (let r = 1; r < matrix.length; r += 1) {
    const cells = matrix[r]
    const roundNumber = Number(cells[roundIdx])
    if (!Number.isInteger(roundNumber) || roundNumber < 1) {
      return {
        rounds: [],
        error: `Row ${r + 1}: round must be a positive integer.`,
      }
    }

    const cellAt = (name: string) => {
      const idx = indexOf(name)
      return idx === -1 ? "" : (cells[idx] ?? "").trim()
    }

    const fields = {} as Variation["fields"]
    for (const field of COPY_FIELDS) {
      const mode = copyMode(cellAt(`${field}_mode`))
      const prompt = cellAt(`${field}_prompt`)
      if (mode === "prompt" && !prompt) {
        return {
          rounds: [],
          error: `Row ${
            r + 1
          }: ${field} is set to prompt but has no prompt text.`,
        }
      }
      fields[field] = { mode, prompt: mode === "prompt" ? prompt : "" }
    }
    for (const field of SIGNATURE_FIELDS) {
      fields[field] = { mode: signatureMode(cellAt(`${field}_mode`)) }
    }

    const variation: Variation = {
      variationName: cells[nameIdx]?.trim() || undefined,
      fields,
    }

    const existing = roundsByNumber.get(roundNumber)
    if (existing) {
      existing.variations.push(variation)
    } else {
      roundsByNumber.set(roundNumber, {
        roundNumber,
        variations: [variation],
      })
    }
  }

  const rounds = [...roundsByNumber.values()].sort(
    (a, b) => a.roundNumber - b.roundNumber,
  )

  // Round numbers must be sequential starting at 1 (backend requirement).
  for (let i = 0; i < rounds.length; i += 1) {
    if (rounds[i].roundNumber !== i + 1) {
      return {
        rounds: [],
        error: "Round numbers must be unique and sequential starting at 1.",
      }
    }
  }

  return { rounds }
}
